import React from 'react';
import styled from 'styled-components';
import {tint} from 'theme/helper';
import {GenericFields} from 'components/customer/Product/Specs/GenericFields';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';

interface DrawerFieldsRowProps {
    fieldset: Fieldset;
    hasPreview?: boolean;
}

// Drawer Amount and the settings that go with it, laid out like the hardware
// defaults at the foot of the same card: label above its control, side by side.
export const DrawerFieldsRow = ({
    fieldset,
    hasPreview = false,
}: DrawerFieldsRowProps) => {
    const compact = useCompactLayout();

    if (!fieldset?.fields?.length) return null;

    return (
        <Row data-cy="drawer-fields-row" $compact={compact}>
            <GenericFields
                fields={fieldset.fields}
                fieldsetName={fieldset.name}
                fieldsetLayout={fieldset.options?.layout}
                fieldsetTitle={false}
                preview={hasPreview}
                colSpanFull
                stackFields
            />
        </Row>
    );
};

// the gutter every row in the card shares once folded
export const COMPACT_PAD = 10;

const Row = styled.div<{$compact: boolean}>`
    /* a column apiece out of the three this fieldset can hold, so a field
       hidden by the product leaves a gap rather than widening the rest */
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding: 11px ${({$compact}) => ($compact ? COMPACT_PAD : 18)}px;
    border-top: 1px solid ${({theme}) => tint(theme.colors.primary?.main, 0.12)};

    ${({$compact}) =>
        $compact
            ? `grid-template-columns: repeat(2, 1fr);
               gap: 10px;
               /* a wrapped label must not drop its own control */
               align-items: end;`
            : ''}

    /* each field arrives as its own form group, indented and spaced to stack
       down a column of rows */
    > div {
        min-width: 0;
        margin-bottom: 0;
        padding-left: 0;
    }

    .row {
        margin-left: 0;
        margin-right: 0;
    }

    [class*='col-'] {
        padding-left: 0;
        padding-right: 0;
    }

    /* FormGroup indents 10px, setting these in from the collapsible rows */
    [class*='col-'] > div {
        padding-left: 0;
    }

    /* every control sizes itself to its contents, which leaves the columns
       ragged. The width is set inline, so filling the column outranks it */
    .input-group {
        width: 100% !important;
    }

    /* pinned absolute top right by default; bring it beside the label text.
       It stands taller than the label's own line, so it is left to overhang
       rather than grow the label, which would drop the control of a field
       that has one below the ones that do not */
    label .more-info {
        position: relative !important;
        top: auto;
        right: auto;
        left: auto;
        display: inline-flex;
        vertical-align: middle;
        margin: -3px 0 -3px 4px;
    }

    label .more-info button {
        padding: 0;
        line-height: 0;
    }

    /* the same label metrics the hardware defaults use */
    label {
        font-size: 13.6px;
        font-weight: 700;
        padding: 7px 0;
        margin-bottom: 0;
        color: ${({theme}) => theme.colors.primary?.main};

        /* the app reddens a required marker through .cbc-form .form-group,
           which this card sits outside of */
        span {
            color: ${({theme}) => theme.colors.errorLabel?.main};
        }
    }
`;
